import React, { useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { KITCHEN_UNITS } from '../utils/measurementConversions';

export default function RecipeIngredientInput({ 
    ingredient, 
    index, 
    handleIngredientChange, 
    removeIngredient 
}) {
    // Define default units in case import fails
    const defaultUnits = [
        'tsp', 'tbsp', 'cup', '1/2cup', '1/4cup',
        'g', 'kg', 'ml', 'l', 'unit', 'whole'
    ];

    // Use KITCHEN_UNITS if available, otherwise use default units
    const units = KITCHEN_UNITS ? Object.values(KITCHEN_UNITS) : defaultUnits;

    // Autocomplete state
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
    const [isTyping, setIsTyping] = useState(false);
    const [previousValue, setPreviousValue] = useState('');
    const [isSelectingManually, setIsSelectingManually] = useState(false);
    const inputRef = useRef(null);

    const handleNameChange = async (value) => {
        // Store the previous value when starting to type
        if (!isTyping && ingredient.name) {
            setPreviousValue(ingredient.name);
        }
        
        setIsTyping(true);
        handleIngredientChange(index, 'name', value);
        
        // Search for suggestions if value length >= 2
        if (value.length >= 2) {
            try {
                const response = await axios.get(`${API_BASE_URL}/cupboard/search/ingredients`, {
                    params: { q: value, limit: 10 }
                });
                
                if (response.data.success) {
                    setSuggestions(response.data.ingredients);
                    setShowSuggestions(true);
                    setActiveSuggestionIndex(-1);
                }
            } catch (error) {
                console.error('Error searching ingredients:', error);
                setSuggestions([]);
                setShowSuggestions(false);
            }
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setIsSelectingManually(true);
        handleIngredientChange(index, 'name', suggestion.name);
        setShowSuggestions(false);
        setSuggestions([]);
        setIsTyping(false);
        setPreviousValue('');
        
        // Reset the manual selection flag after a short delay
        setTimeout(() => {
            setIsSelectingManually(false);
        }, 100);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextIndex = activeSuggestionIndex < suggestions.length - 1 ? activeSuggestionIndex + 1 : 0;
            setActiveSuggestionIndex(nextIndex);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const nextIndex = activeSuggestionIndex > 0 ? activeSuggestionIndex - 1 : suggestions.length - 1;
            setActiveSuggestionIndex(nextIndex);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeSuggestionIndex >= 0 && suggestions[activeSuggestionIndex]) {
                handleSuggestionClick(suggestions[activeSuggestionIndex]);
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            // Revert to previous value on escape
            if (previousValue) {
                handleIngredientChange(index, 'name', previousValue);
            }
            setIsTyping(false);
            setPreviousValue('');
        }
    };

    const handleBlur = () => {
        // If user is manually selecting, don't do anything
        if (isSelectingManually) {
            return;
        }

        // Delay to allow clicking on suggestions
        setTimeout(() => {
            // Double-check that we're not manually selecting
            if (isSelectingManually) {
                return;
            }

            if (isTyping) {
                if (showSuggestions && suggestions.length > 0) {
                    // Check if current input exactly matches a suggestion
                    const exactMatch = suggestions.find(
                        s => s.name.toLowerCase() === ingredient.name.toLowerCase()
                    );
                    
                    if (exactMatch) {
                        // Use exact match
                        handleIngredientChange(index, 'name', exactMatch.name);
                    } else {
                        // Auto-select best match only if no manual selection was made
                        const bestMatch = suggestions[0];
                        if (bestMatch) {
                            handleIngredientChange(index, 'name', bestMatch.name);
                        }
                    }
                } else {
                    // No suggestions found, revert to previous value if it exists
                    if (previousValue) {
                        handleIngredientChange(index, 'name', previousValue);
                    }
                }
            }
            
            setShowSuggestions(false);
            setIsTyping(false);
            setPreviousValue('');
        }, 300);
    };

    const handleFocus = () => {
        if (ingredient.name && ingredient.name.length >= 2 && suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };

    return (
        <div className="ingredient-input" style={{ marginBottom: '15px', position: 'relative', border: '1px solid #e0e0e0', padding: '12px', borderRadius: '6px', backgroundColor: '#fafafa' }}>
            {/* Primary ingredient information row */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Ingredient name..."
                        value={ingredient.name || ''}
                        onChange={(e) => handleNameChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        autoComplete="off"
                        style={{ 
                            width: '200px',
                            borderColor: isTyping && showSuggestions ? '#007bff' : '#ddd'
                        }}
                    />
                    {showSuggestions && suggestions.length > 0 && (
                        <div className="suggestions-dropdown" style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderTop: 'none',
                            borderRadius: '0 0 4px 4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            <div style={{
                                padding: '8px 12px',
                                backgroundColor: '#f8f9fa',
                                borderBottom: '1px solid #eee',
                                fontSize: '12px',
                                color: '#666'
                            }}>
                                {suggestions.length} ingredient{suggestions.length !== 1 ? 's' : ''} found - Click to select or press Escape to cancel
                            </div>
                            {suggestions.map((suggestion, suggestionIndex) => (
                                <div
                                    key={suggestionIndex}
                                    className={`suggestion-item ${
                                        activeSuggestionIndex === suggestionIndex ? 'active' : ''
                                    }`}
                                    onMouseDown={() => {
                                        // Use onMouseDown instead of onClick to fire before onBlur
                                        setIsSelectingManually(true);
                                        handleSuggestionClick(suggestion);
                                    }}
                                    onMouseEnter={() => setActiveSuggestionIndex(suggestionIndex)}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        backgroundColor: activeSuggestionIndex === suggestionIndex ? '#e9ecef' : 'white',
                                        borderBottom: suggestionIndex < suggestions.length - 1 ? '1px solid #eee' : 'none'
                                    }}
                                >
                                    {suggestion.name}
                                    {suggestionIndex === 0 && (
                                        <span style={{ 
                                            fontSize: '10px', 
                                            color: '#007bff', 
                                            marginLeft: '8px' 
                                        }}>
                                            (Best match)
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Amount"
                    value={ingredient.amount || ''}
                    onChange={(e) => handleIngredientChange(index, 'amount', e.target.value)}
                    style={{ width: '80px' }}
                />
                <select
                    value={ingredient.unit || ''}
                    onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                    style={{ minWidth: '80px' }}
                >
                    <option value="">Select unit</option>
                    {units.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                    ))}
                </select>
                
                {ingredient.unit === 'other' && (
                    <input
                        type="text"
                        placeholder="Custom unit"
                        value={ingredient.customUnit || ''}
                        onChange={(e) => handleIngredientChange(index, 'customUnit', e.target.value)}
                        style={{ width: '100px' }}
                    />
                )}
                
                <button 
                    type="button" 
                    onClick={() => removeIngredient(index)}
                    style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        marginLeft: 'auto'
                    }}
                >
                    Remove
                </button>
            </div>
            
            {/* Optional display text and preparation row */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                        Display Text (Optional) - e.g., "2 onions, chopped"
                    </label>
                    <input
                        type="text"
                        placeholder='e.g., "3 cloves garlic" or "2 chicken breasts"'
                        value={ingredient.displayText || ''}
                        onChange={(e) => handleIngredientChange(index, 'displayText', e.target.value)}
                        style={{ width: '100%', fontSize: '13px' }}
                    />
                    <small style={{ fontSize: '11px', color: '#999', display: 'block', marginTop: '2px' }}>
                        Leave blank to show structured format (amount + unit + name)
                    </small>
                </div>
                <div style={{ flex: '0 0 200px' }}>
                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                        Preparation (Optional)
                    </label>
                    <input
                        type="text"
                        placeholder="e.g., chopped, diced, minced"
                        value={ingredient.preparation || ''}
                        onChange={(e) => handleIngredientChange(index, 'preparation', e.target.value)}
                        style={{ width: '100%', fontSize: '13px' }}
                    />
                    <small style={{ fontSize: '11px', color: '#999', display: 'block', marginTop: '2px' }}>
                        For structured format only
                    </small>
                </div>
            </div>
        </div>
    );
}